import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraInput, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraOverlayFooter } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { dateValue, vehicleSaveError } from '../../../Delivery/Registries/contractVehicleUi';
import { useVehicleBrowse } from '../../../Delivery/Registries/vehicleIdentityHooks';
import { useVehicleLineage } from './hooks';
import { monthYear, VehicleRefLink } from './vehicleUi';
export function LineageEditorDialog({ vehicleId, canWrite, onClose }) {
    const { query, save, archive } = useVehicleLineage(vehicleId, canWrite);
    const browse = useVehicleBrowse();
    const [successor, setSuccessor] = useState('');
    const [date, setDate] = useState('');
    const [source, setSource] = useState('');
    const [artifact, setArtifact] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const pending = save.isPending || archive.isPending;
    if (!canWrite) return null;
    const submit = async ()=>{
        if (pending) return;
        if (!successor) {
            setError('Choose a successor vehicle.');
            return;
        }
        if (!source.trim()) {
            setError('A source basis is required.');
            return;
        }
        setError('');
        try {
            await save.mutateAsync({
                id: crypto.randomUUID(),
                predecessorVehicleId: vehicleId,
                successorVehicleId: successor,
                relation: 'SUCCESSOR',
                effectiveDate: dateValue(date),
                sourceBasis: source.trim(),
                sourceArtifactId: artifact.trim()
            });
            setSuccessor('');
            setSource('');
            setArtifact('');
            setDate('');
        } catch (err) {
            setError(vehicleSaveError(err));
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        title: "Vehicle lineage",
        description: "Record a sourced successor. To correct lineage, archive the old record and save a new one.",
        onOpenChange: (open)=>{
            if (!open && !pending) onClose();
        },
        testId: Testids.registryVehicle.lineageDialog,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                query.isPending ? /*#__PURE__*/ _jsx("output", {
                    children: "Loading lineage…"
                }) : query.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void query.refetch(),
                        children: "Retry lineage"
                    }),
                    children: "Could not load lineage."
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        (query.data?.edges ?? []).filter((edge)=>!edge.archivedAt).map((edge)=>/*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.registryVehicle.lineageRow,
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        children: [
                                            /*#__PURE__*/ _jsx(VehicleRefLink, {
                                                value: edge.predecessor
                                            }),
                                            " →",
                                            ' ',
                                            /*#__PURE__*/ _jsx(VehicleRefLink, {
                                                value: edge.successor
                                            }),
                                            edge.effectiveDate && ` · ${monthYear(edge.effectiveDate)}`
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        size: "meta",
                                        tone: "muted",
                                        children: [
                                            "Source: ",
                                            edge.sourceBasis || edge.sourceArtifact?.name || 'Linked artifact'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        disabled: pending,
                                        onClick: async ()=>{
                                            if (!reason.trim()) {
                                                setError('An archive reason is required.');
                                                return;
                                            }
                                            setError('');
                                            try {
                                                await archive.mutateAsync({
                                                    id: edge.id,
                                                    reason: reason.trim()
                                                });
                                                setReason('');
                                            } catch (err) {
                                                setError(vehicleSaveError(err));
                                            }
                                        },
                                        children: "Archive lineage"
                                    })
                                ]
                            }, edge.id)),
                        query.data?.edges.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No lineage recorded."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Archive reason",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Archive reason",
                        value: reason,
                        disabled: pending,
                        onChange: (e)=>setReason(e.target.value)
                    })
                }),
                browse.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void browse.refetch(),
                        children: "Retry vehicles"
                    }),
                    children: "Could not load successor vehicles."
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Successor vehicle",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Successor vehicle",
                        disabled: pending || browse.isPending,
                        value: successor,
                        onChange: (e)=>setSuccessor(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: browse.isPending ? 'Loading vehicles…' : 'Choose a vehicle'
                            }),
                            browse.data?.filter((v)=>v.id !== vehicleId).map((v)=>/*#__PURE__*/ _jsx("option", {
                                    value: v.id,
                                    children: v.name
                                }, v.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Effective date",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Effective date",
                        type: "date",
                        value: date,
                        disabled: pending,
                        onChange: (e)=>setDate(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Source basis",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Source basis",
                        value: source,
                        disabled: pending,
                        onChange: (e)=>setSource(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Source artifact ID",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Source artifact ID",
                        value: artifact,
                        disabled: pending,
                        onChange: (e)=>setArtifact(e.target.value)
                    })
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    role: "alert",
                    children: error
                }),
                /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                    confirmLabel: save.isPending ? 'Saving…' : 'Save successor',
                    confirmDisabled: pending || query.isPending || query.isError || browse.isPending || browse.isError,
                    onConfirm: ()=>void submit(),
                    cancelProps: {
                        disabled: pending
                    },
                    onCancel: onClose
                })
            ]
        })
    });
}
